import { SectionWithHeading } from '@/ui/SectionHeading';
import { useGetCompensationCurrenciesQuery } from '../../../store/query/compensation-settings';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { useGetAtsOfficesQuery } from '@/store/query/offices';
import { useGetAtsDepartmentsQuery } from '@/store/query/departments';
import { useLazyGetAllOfferTemplatesQuery } from '@/store/query/offer-letter-templates';
import { useField } from 'formik';
import { Box, Flex, Link, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DepartmentSingleValue,
  SelectOptionWithAvatar,
  SelectParentChildRootOption,
  SelectWithDescriptionOption,
} from '@/components/formBuilder/SelectComponents';
import { useLazyGetCustomFieldsQuery } from '@/store/query/custom-fields-settings';
import { generateCustomFormConfig } from '@/formConfigs/generateCustomFormConfig';
import {
  attachRelUserAvatarRenderer,
  resolveStaticFieldVariable,
} from './offerFormHelpers';
import { useKFlags } from '@/hooks/use-k-flags';
import { isEqual } from 'lodash-es';
import { LinkExternal01 } from '@kula-ai/icons-core';
import { EmptyResults } from '../empty-results';
import { NoTokens } from '@/assets/icons/ats/empty';
import { KText } from '@/ui-library/components/text';
import { KTag } from '@/ui-library/components';
import AssociateReq from '@/components/shared/requisitions/associate-req/AssociateReq';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import {
  getRequisitionStateOffer,
  REJECTED_OFFER_STATUSES,
} from '@/utils/requisition-state-helper';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { KSelectField } from '@/ui-library/components/organisms/KFields/KSelectField';
import { COLORS } from '@kula-ai/design-tokens';
import { FIELD_KIND, SECTION_ID } from '@/constants/offer-field-constants';
import {
  AWARD_TYPE_OPTIONS,
  EQUITY_TYPE_OPTIONS,
} from '@/constants/compensation';
import { EMAIL_REGEX } from '@/constants/regex';
import type { SectionId } from '@/constants/offer-field-constants';
import {
  readJobWorkTypeId,
  useWorkTypeOptions,
  workTypeScopeParam,
} from '@/api/work-types';

// Constants
const offerSectionProps = (isOfferV1 = false) => ({
  background: 'secondary-50',
  containerStyles: {
    mb: !isOfferV1 ? '5px' : '16px',
    padding: !isOfferV1 ? '10px 16px' : '5px',
  },
  contentStyles: {
    bg: 'white',
    borderRadius: '0 0 6px 6px',
  },
});

const ALLOWED_REQUISITION_STATUSES = ['approved', 'created', 'open'];

const SECTION_LABEL_BY_ID: Record<SectionId, string> = {
  [SECTION_ID.OFFER_DETAILS]: 'Offer details',
  [SECTION_ID.ADDITIONAL_COMPENSATION]: 'Additional compensation',
  [SECTION_ID.CANDIDATE_DETAILS]: 'Candidate details',
  [SECTION_ID.JOB_DETAILS]: 'Job details',
  [SECTION_ID.CUSTOM]: 'Custom fields',
};
const SECTION_DOM_ID_BY_ID: Record<SectionId, string> = {
  [SECTION_ID.OFFER_DETAILS]: 'offerDetailsSection',
  [SECTION_ID.ADDITIONAL_COMPENSATION]: 'additionalCompensationSection',
  [SECTION_ID.CANDIDATE_DETAILS]: 'candidateSection',
  [SECTION_ID.JOB_DETAILS]: 'jobDetailsSection',
  [SECTION_ID.CUSTOM]: 'customSection',
};
// BE field ids whose FE fieldName/name does not match 1:1.
const BE_ID_TO_FE_IDENTIFIER: Record<string, string> = {
  office: 'ats_office',
  department: 'ats_department',
};

const CURRENCY_SELECT_STYLES = {
  control: (base: any) => ({
    ...base,
    backgroundColor: COLORS.secondary[25],
    borderTopRightRadius: '6px',
    borderBottomRightRadius: '6px',
    borderTopLeftRadius: '0',
    borderBottomLeftRadius: '0',
    boxShadow: 'none',
    height: '44px',
    width: '200px',
    '&:hover': {},
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
};

const SELECT_STYLES = [
  {
    type: 'control',
    newStyles: {
      minWidth: '180px',
      border: 'none',
      borderLeft: '2px solid #EAEAEA',
      borderRadius: 'none',
      '&:hover': {
        cursor: 'pointer',
        border: 'none',
        borderLeft: '2px solid #EAEAEA',
        boxShadow: 'none',
        outline: 'none',
      },
      '&:focus': {
        border: 'none',
        outline: 'none',
      },
    },
  },
];

const TIME_UNIT_OPTIONS = [
  { label: 'Month', value: 'month' },
  { label: 'Quarter', value: 'quarter' },
  { label: 'Year', value: 'year' },
];

const SALARY_FREQUENCY_OPTIONS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi_weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'one_time', label: 'One time' },
];

// EQUITY_TYPE_OPTIONS / AWARD_TYPE_OPTIONS are shared with the job form so equity
// advertised on a job maps 1:1 onto the offer.

// Both equity-amount fields share name:'amount', so only one survives schema
// construction (last-wins). This validation is award_type-agnostic and derives
// its label/message from the sibling award_type so the correct inline error
// shows for either variant. `.nullable()` lets a null value reach the test
// instead of tripping Yup's built-in "amount cannot be null" message.
export const equityAmountValidation = Yup.mixed()
  .nullable()
  .test('equityAmountRequired', 'Equity amount is required', function (value) {
    const isFilled =
      value !== null && value !== undefined && `${value}`.trim().length > 0;
    if (isFilled) return true;
    const label =
      this.parent?.award_type === 'money' ? 'Equity money' : 'Equity units';
    return this.createError({ message: `${label} is required` });
  });

// Helper Functions
const createCurrencySelectComponent = (
  fieldName: string,
  currencies: any[],
  isCurrenciesLoading: boolean
) => {
  const CurrencySelect = () => {
    const [, { value, initialValue }] = useField(fieldName);
    return (
      <KSelectField
        item={{
          name: fieldName,
          type: 'kSelect',
          isRequired: false,
          defaultValue: value || initialValue,
          options: currencies,
          isClearable: false,
          placeholder: 'Select currency',
          newStyles: SELECT_STYLES,
          labelKey: 'currency',
          valueKey: 'id',
          isLoading: isCurrenciesLoading,
          menuPortalTarget: document.body,
          customStyles: {
            control: CURRENCY_SELECT_STYLES.control,
            menuPortal: CURRENCY_SELECT_STYLES.menuPortal,
          },
        }}
      />
    );
  };
  CurrencySelect.displayName = 'CurrencySelect';
  return CurrencySelect;
};

const createUnitSelectComponent = (
  fieldName: string,
  placeholder: string = 'Select cliff unit'
) => {
  const UnitSelect = () => {
    const [, { value, initialValue }] = useField(fieldName);
    return (
      <KSelectField
        item={{
          name: fieldName,
          type: 'kSelect',
          defaultValue: value || initialValue,
          isRequired: false,
          options: TIME_UNIT_OPTIONS,
          placeholder,
          isClearable: false,
          menuPortalTarget: document.body,
          customStyles: {
            control: CURRENCY_SELECT_STYLES.control,
            menu: CURRENCY_SELECT_STYLES.menu,
            menuPortal: CURRENCY_SELECT_STYLES.menuPortal,
          },
        }}
      />
    );
  };
  UnitSelect.displayName = 'UnitSelect';
  return UnitSelect;
};

const createCurrencyInputField = (
  name: string,
  label: string,
  fieldName: string,
  currencyFieldName: string,
  currencies: any[],
  CurrencySelectComponent: React.ComponentType,
  placeholder?: string
) => ({
  type: 'kInput',
  name,
  label,
  formatType: 'currency',
  isRequired: false,
  containerStyles: {
    width: '100%',
    position: 'relative',
  },
  customRightElementProps: {
    right: 0,
  },
  CustomRightElement: CurrencySelectComponent,
  placeholder,
  fieldName,
  currencyFieldName,
  currencies,
});

const applyFieldDetails = (
  fields: any[],
  groupId: string,
  getFieldDetails: (groupId: string, fieldId: string) => any
) => {
  return fields
    .map((field) => {
      const fieldId = field?.fieldName || field?.name;
      const details = getFieldDetails(groupId, fieldId);
      // Preserve original isRequired if skipValidation is true
      const isRequired = field?.skipValidation
        ? field?.isRequired
        : details?.required || field?.isRequired;
      return {
        ...field,
        isRequired,
        tokenName: details?.variable,
      };
    })
    .filter((field) => {
      const fieldId = field?.fieldName || field?.name;
      const details = getFieldDetails(groupId, fieldId);
      return !details?.hidden || field?.skipValidation || field?.isConditional;
    });
};

export const useOfferInitiationConfig = ({
  //  setFormValidity,
  tabIndex,
  offerStaticFields = [],
  offerData,
  _formRef,
  isOfferV1 = false,
}) => {
  const { data: { currencies = [] } = {}, isLoading: isCurrenciesLoading } =
    useGetCompensationCurrenciesQuery({});

  const [filterData, setFilterData] = useState({
    departments: [],
    offices: [],
  });

  const currUser = useSelector(selectCurrUser);
  const { feOfferFieldReorder } = useKFlags();
  // Fall back to legacy if BE hasn't shipped the custom section yet.
  const offerFieldReorderEnabled =
    !!feOfferFieldReorder &&
    !!offerStaticFields?.some(
      (section: any) => section?.id === SECTION_ID.CUSTOM
    );
  const { currentJob } = useSelector(getAtsJobCreate);
  const currentJobData = currentJob?.data || {};
  const { basic_info: { requisitions = [] } = {} } = currentJobData || {};

  // Filter requisitions to only include 'approved', 'created', or 'open' statuses
  const filteredRequisitions = useMemo(() => {
    return (
      requisitions?.filter(
        (req) =>
          ALLOWED_REQUISITION_STATUSES.includes(req?.status) &&
          !req?.has_association
      ) || []
    );
  }, [requisitions]);

  const jobAssociatedRequisitions = filteredRequisitions?.some(
    (req) => req.role_name
  ) as boolean;
  // Extract requisitions from offer data if available
  const { requisition } = offerData || {};
  const showInputField = offerData?.id && !offerData?.requisition?.id;

  const [showLegacyFlowDynamically, setShowLegacyFlowDynamically] =
    useState(false);

  // Use useMemo to compute initial value
  const initialRequisitions = useMemo(() => {
    if (offerData?.requisition?.id) {
      return [requisition];
    }

    // Rejected offers with no linked req should start with a clean slate
    const isRejectedWithNoReq =
      showInputField &&
      REJECTED_OFFER_STATUSES.includes(offerData?.offer_status) &&
      currUser?.requisition?.enabled;

    if (isRejectedWithNoReq) return [];

    if (jobAssociatedRequisitions && filteredRequisitions[0]) {
      return [filteredRequisitions[0]];
    }

    return [];
  }, [
    offerData?.requisition?.id,
    offerData?.offer_status,
    showInputField,
    currUser?.requisition?.enabled,
    requisition,
    filteredRequisitions,
    jobAssociatedRequisitions,
  ]);

  const [selectedRequisitions, setSelectedRequisitions] = useState([]);

  const { shouldUseLegacyFlow, userHasReqEnabled, shouldUseAssociateReq } =
    getRequisitionStateOffer({
      currUser,
      requisitions: selectedRequisitions,
      showLegacyFlowDynamically,
      showInputField,
      offerStatus: offerData?.offer_status,
    });

  const [getTemplates, { data: { data: templates = [] } = {} }] =
    useLazyGetAllOfferTemplatesQuery();

  const isNewOfferFields = true;
  const allowNewRequisition = isNewOfferFields && userHasReqEnabled;

  const jobEmploymentType = currentJobData?.basic_info?.employment_type;
  const { isWorkTypesEnabled } = useWorkTypeOptions();
  const jobWorkTypeId = readJobWorkTypeId(currentJobData?.basic_info ?? {});

  useEffect(() => {
    if (tabIndex === 0) return;
    getTemplates({
      params: {
        filter_by: {
          department_ids: filterData.departments,
          office_ids: filterData.offices,
          // Scope the template list to the job's work type. Flag off keeps the
          // legacy enum. BACKEND REQUIRED: filter_by on /ats_offer_templates
          // slices to %i[department_ids office_ids employment_type
          // ats_offer_template_name], so flag-on the key is dropped and the
          // list comes back unnarrowed — the same result a custom-typed job
          // gets today, since its employment_type is null.
          ...workTypeScopeParam(
            isWorkTypesEnabled,
            {
              atsWorkTypeIds: [jobWorkTypeId],
              employmentTypes: [jobEmploymentType],
            },
            // This endpoint slices on the singular key.
            { legacyKey: 'employment_type' }
          ),
        },
        items: 100,
      },
    });
  }, [
    getTemplates,
    filterData,
    jobEmploymentType,
    jobWorkTypeId,
    isWorkTypesEnabled,
    tabIndex,
  ]);

  const { data: offices } = useGetAtsOfficesQuery({});
  const { data: departments } = useGetAtsDepartmentsQuery({});
  const [customForm, setCustomForm] = useState([]);
  const [params, setParams] = useState({
    department_ids: [],
    office_ids: [],
    subject_type: 'ats_offer',
  });

  const [getCustomFields, { data: { ats_form_fields = [] } = {} }] =
    useLazyGetCustomFieldsQuery({});

  // Seed the dept/office scope from the offer as soon as it loads, before the in-form field effect
  // (customFieldsWrapper) fires. Otherwise the first fetch uses empty params and returns only the
  // globally-scoped custom fields, so an offer's dept/office-scoped inline fields don't render on the
  // first template selection — the recruiter saves them null and document generation gets bad tokens
  // (CORE-4829). Same param shape as customFieldsWrapper so it doesn't cause an extra refetch.
  useEffect(() => {
    const dept = offerData?.ats_department_id;
    const office = offerData?.ats_office_id;
    setParams((prev) => ({
      ...prev,
      ...(dept ? { department_ids: [dept] } : {}),
      ...(office ? { office_ids: office } : {}),
    }));
  }, [offerData?.ats_department_id, offerData?.ats_office_id]);

  useEffect(() => {
    getCustomFields({ params });
  }, [getCustomFields, params]);

  useEffect(() => {
    if (ats_form_fields?.length >= 0) {
      // generateCustomFormConfig keys form items by `custom_${id}`. Composer
      // returns row.id as `id` and the cf id as `ats_custom_field_id` for
      // custom rows; downstream lookups (customFieldById, customIdsInCustomSection,
      // buildFieldsFromBeF) all key by cf id under reorder-on. Reseat id to
      // ats_custom_field_id so name=`custom_<cf_id>` and the three lookups align.
      const remapped = (ats_form_fields || []).map((f: any) =>
        f?.field_kind === FIELD_KIND.CUSTOM &&
        f?.ats_custom_field_id !== null &&
        f?.ats_custom_field_id !== undefined
          ? { ...f, id: f.ats_custom_field_id }
          : f
      );
      const res = generateCustomFormConfig(
        {
          ats_custom_fields: remapped,
        },
        true,
        false,
        'offer_cf',
        'v2'
      );
      // rel_user ("Users") fields render with avatars in the dropdown +
      // selected chips — mirrors useCreateRequisitions.
      setCustomForm(
        attachRelUserAvatarRenderer(
          remapped,
          res?.custom ?? [],
          SelectOptionWithAvatar
        )
      );
    }
  }, [ats_form_fields]);

  const usersList = useSelector(selectUsers);

  const getStaticFieldDetails = (groupId, fieldId) => {
    return offerStaticFields
      ?.find((i) => i?.id === groupId)
      ?.fields?.find((i) => i?.id === fieldId);
  };

  const getFieldDetails = (groupId, fieldId) => {
    if (!isNewOfferFields)
      return { hidden: false, required: false, variable: '' };
    const inGroup = getStaticFieldDetails(groupId, fieldId);
    // hidden/required stay group-scoped (they drive this section's rendering); the
    // variable falls back to a global lookup so the token still maps when BE placed
    // the static field in another section, else getFilledValues drops the value.
    return {
      hidden: inGroup?.hidden || false,
      required: inGroup?.required || false,
      variable: resolveStaticFieldVariable(offerStaticFields, groupId, fieldId),
    };
  };

  // Helper function to reorder fields based on static_fields order
  const reorderFieldsByStaticFields = (fields, groupId) => {
    if (!isNewOfferFields || !offerStaticFields?.length || !fields?.length) {
      return fields;
    }

    const staticFieldSection = offerStaticFields.find(
      (section) => section.id === groupId
    );

    if (!staticFieldSection?.fields?.length) {
      return fields;
    }

    // Separate conditional fields from regular fields
    const conditionalFields = fields.filter((field) => field?.isConditional);
    const regularFields = fields.filter((field) => !field?.isConditional);

    // Separate fields that should follow other fields (e.g., salary warning)
    const fieldsThatFollow = regularFields.filter(
      (field) => field?.followsField
    );
    const regularFieldsWithoutFollowers = regularFields.filter(
      (field) => !field?.followsField
    );

    // Separate fields with and without identifiers (only for regular fields without followers)
    const fieldMap = new Map();
    const fieldsWithoutIdentifier = [];

    regularFieldsWithoutFollowers.forEach((field) => {
      const identifier = field?.fieldName || field?.name;
      if (identifier) {
        fieldMap.set(identifier, field);
      } else {
        fieldsWithoutIdentifier.push(field);
      }
    });

    // Helper function to get followers for a field
    const getFollowers = (field) => {
      const fieldIdentifier = field?.fieldName || field?.name;
      return fieldsThatFollow.filter(
        (f) => f?.followsField === fieldIdentifier
      );
    };

    // Reorder fields based on static_fields order
    const reorderedFields = [];
    const processedFields = new Set();

    staticFieldSection.fields.forEach((staticField) => {
      const field = fieldMap.get(staticField.id);
      if (field) {
        processedFields.add(field);
        reorderedFields.push(field);
        // Add followers immediately after the field
        const followers = getFollowers(field);
        followers.forEach((follower) => {
          reorderedFields.push(follower);
          processedFields.add(follower);
        });
      }
    });

    // Flag-on: skip fields placed in other sections. Translate BE id → FE identifier
    // so e.g. 'office' matches the 'ats_office' fieldMap key.
    const idsInOtherSections = offerFieldReorderEnabled
      ? new Set(
          offerStaticFields
            .filter((section) => section.id !== groupId)
            .flatMap((section) =>
              (section.fields || [])
                .filter((f) => f?.id !== null && f?.id !== undefined)
                .map(
                  (f) => BE_ID_TO_FE_IDENTIFIER[String(f.id)] || String(f.id)
                )
            )
        )
      : new Set<string>();

    fieldMap.forEach((field, identifier) => {
      if (processedFields.has(field)) return;
      if (idsInOtherSections.has(String(identifier))) return;
      reorderedFields.push(field);
      processedFields.add(field);
      const followers = getFollowers(field);
      followers.forEach((follower) => {
        if (!processedFields.has(follower)) {
          reorderedFields.push(follower);
          processedFields.add(follower);
        }
      });
    });

    // Add any remaining followers that weren't associated with any field
    const unusedFollowers = fieldsThatFollow.filter(
      (f) => !processedFields.has(f)
    );

    // Return regular fields first, then conditional fields
    return [
      ...fieldsWithoutIdentifier,
      ...reorderedFields,
      ...unusedFollowers,
      ...conditionalFields,
    ];
  };

  // Create currency select components using factory
  const SalaryCurrencyId = createCurrencySelectComponent(
    'salary_currency_id',
    currencies,
    isCurrenciesLoading
  );

  const AdditionalCompensationCurrencyId = createCurrencySelectComponent(
    'additional_compensation_currency_id',
    currencies,
    isCurrenciesLoading
  );

  const StrikePriceCurrencyId = createCurrencySelectComponent(
    'strike_price_currency_id',
    currencies,
    isCurrenciesLoading
  );

  // Extract requisition fields to be placed first
  const requisitionFields = [
    {
      type: 'custom',
      Field: () => {
        const [, , { setValue }] = useField('useLegacy');

        useEffect(() => {
          if (shouldUseLegacyFlow) {
            setValue(true);
          } else {
            setValue(undefined);
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [shouldUseLegacyFlow, setValue]);

        return null;
      },
    },
    isNewOfferFields && userHasReqEnabled
      ? {
          type: 'wrapper',
          Wrapper: Flex,
          wrapperProps: {
            justifyContent: 'start',
            flexDirection: 'column',
          },
          fields: [
            shouldUseLegacyFlow && {
              name: 'req_id',
              label: 'Req ID',
              type: 'input',
              isRequired: true,
              fieldName: 'req_id',
            },
            shouldUseLegacyFlow && {
              type: 'custom',
              name: 'associate_req_id',
              validation: Yup.string().required('Requisitions is required'),
              Field: AssociateReq,
              props: {
                requisitions: [],
                multiSelect: false,
                userHasReqEnabled,
                shouldUseLegacyFlow: showInputField,
                setShowLegacyFlowDynamically,
                selectedRequisitions,
                setSelectedRequisitions,
                offerId: offerData?.id,
                buttonText: 'Switch to new requisitions',
              },
            },
          ],
        }
      : null,

    allowNewRequisition && shouldUseAssociateReq
      ? {
          type: 'custom',
          name: 'requisitions_codes',
          isRequired: true,
          validation: Yup.mixed()
            .nullable()
            .test('req-required', 'Requisitions is required', function (value) {
              return Boolean(value) || Boolean(this.parent?.req_id);
            }),
          Field: AssociateReq,
          props: {
            requisitions: selectedRequisitions,
            multiSelect: false,
            userHasReqEnabled,
            shouldUseLegacyFlow: false,
            setShowLegacyFlowDynamically,
            selectedRequisitions,
            setSelectedRequisitions,
            offerId: offerData?.id,
            initialRequisitions,
            offerReq: offerData?.requisition?.id
              ? [offerData?.requisition]
              : [],
          },
        }
      : null,
  ].filter(Boolean);

  const offerDetailsFields = [
    {
      name: 'today_date',
      label: 'Offer date',
      type: 'date',
      disablePreviousDate: true,
      isRequired: false,
      fieldName: 'offer_date',
      styleVersion: 'v2',
    },
    {
      name: 'start_date',
      label: 'Start date',
      type: 'date',
      disablePreviousDate: true,
      isRequired: false,
      fieldName: 'start_date',
      styleVersion: 'v2',
    },
    createCurrencyInputField(
      'salary',
      'Salary',
      'salary',
      'salary_currency_id',
      currencies,
      SalaryCurrencyId,
      'Add proposed salary here'
    ),
    {
      type: 'custom',
      followsField: 'salary',
      Field: () => {
        const [, { value: salaryValue }] = useField('salary');

        const selectedReq =
          selectedRequisitions?.[0] || initialRequisitions?.[0];

        if (!selectedReq?.maximum_salary || !salaryValue) {
          return null;
        }

        const salary = parseFloat(salaryValue.toString().replace(/,/g, ''));
        if (salary <= selectedReq.maximum_salary) {
          return null;
        }

        // Format salary range with requisition's currency
        const currency = selectedReq.salary_currency;
        const symbol = currency?.currency_symbol || '$';
        const code = currency?.currency_code || 'USD';

        const minSalary = selectedReq.minimum_salary;
        const maxSalary = selectedReq.maximum_salary;

        const rangeText = minSalary
          ? `${symbol}${minSalary.toLocaleString()} - ${symbol}${maxSalary.toLocaleString()} ${code}`
          : `${symbol}${maxSalary.toLocaleString()} ${code}`;

        return (
          <Box bg="secondary-25" borderRadius="6px" p="12px">
            <KText size="sm-medium" color="secondary-900">
              This salary is above the requisition range ({rangeText})
            </KText>
            <KText>
              The salary you entered is higher than the range in the
              requisition. You can still send the offer, but the offer approvers
              will be informed.
            </KText>
          </Box>
        );
      },
      skipValidation: true,
    },
    {
      name: 'salary_frequency',
      label: 'Salary frequency',
      type: 'kSelect',
      options: SALARY_FREQUENCY_OPTIONS,
      valueKey: 'value',
      labelKey: 'label',
      fieldName: 'salary_frequency',
    },
    {
      name: 'expiration_date',
      label: 'Offer expiration date',
      type: 'date',
      disablePreviousDate: true,
      isRequired: false,
      fieldName: 'expiration_date',
      styleVersion: 'v2',
    },
    createCurrencyInputField(
      'additional_compensation',
      'Additional bonus (one-time)',
      'additional_compensation',
      'additional_compensation_currency_id',
      currencies,
      AdditionalCompensationCurrencyId
    ),
  ];

  const processedOfferDetailsFields = applyFieldDetails(
    offerDetailsFields,
    'offer_details',
    getFieldDetails
  );

  const orderedOfferDetailsFields = [
    ...requisitionFields,
    ...reorderFieldsByStaticFields(
      processedOfferDetailsFields,
      'offer_details'
    ),
  ];

  const customFieldsWrapper = {
    type: 'custom',
    Field: () => {
      const [, { value: depValue }] = useField('ats_department_id');
      const [, { value: offValue }] = useField('ats_office_id');

      useEffect(() => {
        setParams((prev) => {
          if (!isEqual(prev.office_ids, offValue)) {
            return {
              ...prev,
              office_ids: offValue,
            };
          } else {
            return prev;
          }
        });
      }, [offValue]);

      useEffect(() => {
        setParams((prev) => {
          if (prev.department_ids?.[0] !== depValue && depValue) {
            return {
              ...prev,
              department_ids: [depValue],
            };
          } else {
            return prev;
          }
        });
      }, [depValue]);
    },
  };

  const additionalCompensationFields = [
    {
      name: 'type_of_equity',
      label: 'Type of equity',
      type: 'kSelect',
      options: EQUITY_TYPE_OPTIONS,
      valueKey: 'value',
      labelKey: 'label',
      isRequired: false,
      fieldName: 'type_of_equity',
    },
    {
      name: 'award_type',
      type: 'radio2',
      label: 'Stock award type',
      styles: {
        spacing: '3rem',
        variant: 'ats-primary',
      },
      options: AWARD_TYPE_OPTIONS,
      fieldName: 'award_type',
      skipValidation: true,
    },
    {
      isConditional: true,
      conditionConfig: { field: 'award_type', value: 'units' },
      label: 'Equity units',
      type: 'kInput',
      name: 'amount',
      fieldType: 'number',
      isRequired: false,
      validation: equityAmountValidation,
      containerStyles: {
        width: '100%',
        position: 'relative',
      },
      fieldName: 'amount',
      // todo: hide for now
      // CustomRightElement: () => {
      //   const [, { value, initialValue }] = useField(
      //     `strike_price_currency_id`
      //   );

      //   return (
      //     <SelectField
      //       item={{
      //         name: 'cliff_of_vesting',
      //         type: 'select',
      //         isRequired: false,
      //         defaultValue: value || initialValue,
      //         options: [
      //           { label: 'Month', value: 'month' },
      //           { label: 'Quarter', value: 'quarter' },
      //           { label: 'Year', value: 'year' },
      //         ],
      //         placeholder: 'Select cliff unit',
      //         isClearable: false,
      //         newStyles: [
      //           {
      //             type: 'control',
      //             newStyles: {
      //               minWidth: '180px',
      //               border: 'none',
      //               borderLeft: '2px solid #EAEAEA',
      //               borderRadius: 'none',
      //               '&:hover': {
      //                 cursor: 'pointer',
      //                 border: 'none',
      //                 borderLeft: '2px solid #EAEAEA',
      //                 boxShadow: 'none',
      //                 outline: 'none',
      //               },
      //               '&:focus': {
      //                 border: 'none',
      //                 outline: 'none',
      //               },
      //             },
      //           },
      //         ],
      //       }}
      //       // field={{
      //       // 	value={value}
      //       // }}
      //     />
      //   );
      // },
    },
    {
      isConditional: true,
      conditionConfig: { field: 'award_type', value: 'money' },
      type: 'kInput',
      name: 'amount',
      label: 'Equity money',
      formatType: 'currency',
      fieldType: 'number',
      isRequired: false,
      validation: equityAmountValidation,
      containerStyles: {
        width: '100%',
        position: 'relative',
      },
      fieldName: 'amount',
      currencyFieldName: 'currency_id',
      currencies: currencies,
      customRightElementProps: {
        right: 0,
      },
      CustomRightElement: createCurrencySelectComponent(
        'currency_id',
        currencies,
        isCurrenciesLoading
      ),
    },
    {
      label: 'Equity cliff',
      type: 'kInput',
      name: 'cliff_of_vesting',
      fieldType: 'number',
      placeholder: 'Define cliff period for the equity',
      isRequired: false,
      fieldName: 'equity_cliff_of_vesting',
      containerStyles: {
        width: '100%',
        position: 'relative',
      },
      customRightElementProps: {
        right: 0,
      },
      CustomRightElement: createUnitSelectComponent('unit_of_cliff'),
    },
    {
      label: 'Post cliff vesting cycle',
      type: 'kInput',
      name: 'cycles_after_cliff',
      fieldType: 'number',
      isRequired: false,
      fieldName: 'equity_cycles_after_cliff',
      containerStyles: {
        width: '100%',
        position: 'relative',
      },
      customRightElementProps: {
        right: 0,
      },
      CustomRightElement: createUnitSelectComponent(
        'unit_of_cycle_after_cliff'
      ),
    },
    {
      type: 'kInput',
      name: `period_of_vesting`,
      label: 'Vesting period',
      placeholder: 'Define vesting period for equity',
      fieldType: 'number',
      isRequired: false,
      fieldName: 'equity_vesting_period',
      containerStyles: {
        width: '100%',
        position: 'relative',
      },
      customRightElementProps: {
        right: 0,
      },
      CustomRightElement: createUnitSelectComponent('unit_of_vesting_period'),
    },
    isNewOfferFields
      ? {
          ...createCurrencyInputField(
            'strike_price',
            'Equity strike price',
            'strike_price',
            'strike_price_currency_id',
            currencies,
            StrikePriceCurrencyId
          ),
          isRequired: isNewOfferFields
            ? getStaticFieldDetails('additional_compensation', 'strike_price')
                ?.required
            : false,
          containerStyles: {
            width: '100%',
          },
        }
      : null,
  ];

  const processedAdditionalCompensationFields = applyFieldDetails(
    additionalCompensationFields,
    'additional_compensation',
    getFieldDetails
  );

  const orderedAdditionalCompensationFields = (() => {
    const reordered = reorderFieldsByStaticFields(
      processedAdditionalCompensationFields,
      'additional_compensation'
    );

    // Ensure award_type always comes before conditional fields that depend on it
    const awardTypeField = reordered.find(
      (field) => field?.fieldName === 'award_type'
    );
    const conditionalFields = reordered.filter(
      (field) =>
        field?.isConditional && field?.conditionConfig?.field === 'award_type'
    );
    const otherFields = reordered.filter(
      (field) =>
        field?.fieldName !== 'award_type' &&
        !(
          field?.isConditional && field?.conditionConfig?.field === 'award_type'
        )
    );

    // Reconstruct array: award_type first, then conditional fields, then others
    return [
      ...(awardTypeField ? [awardTypeField] : []),
      ...conditionalFields,
      ...otherFields,
    ];
  })();

  const candidateFields = [
    {
      name: 'candidate_name',
      label: 'Name',
      type: 'kInput',
      maxLength: 250,
      isRequired: false,
    },
    {
      name: 'candidate_email',
      label: 'Email',
      type: 'kInput',
      inputType: 'email',
      isRequired: true,
      validation: Yup.string()
        .email('Valid email address required')
        .matches(EMAIL_REGEX, 'Valid email address required')
        .required('Valid email address required'),
      skipValidation: true,
    },
    {
      name: 'candidate_phone',
      label: 'Phone',
      maxLength: 250,
      type: 'kInput',
      isRequired: false,
    },
    {
      name: 'candidate_address',
      label: 'Address',
      maxLength: 255,
      validation: Yup.string()
        .trim()
        .max(255, 'Max 255 characters allowed')
        .required('Address is required'),
      type: 'textArea',
      isRequired: false,
      styleVersion: 'v2',
    },
  ];

  const processedCandidateFields = applyFieldDetails(
    candidateFields,
    'candidate_details',
    getFieldDetails
  );

  const orderedCandidateFields = reorderFieldsByStaticFields(
    processedCandidateFields,
    'candidate_details'
  );

  const jobFields = [
    {
      name: 'job_title',
      label: 'Job title',
      type: 'kInput',
      maxLength: 250,
      isRequired: false,
      fieldName: 'job_title',
    },
    {
      label: 'Reporting manager',
      type: 'kSelect',
      name: 'reporting_manager_id',
      options: usersList,
      customFormatLabel: SelectOptionWithAvatar,
      components: {
        Option: SelectWithDescriptionOption,
      },
      showAvatar: true,
      descKey: 'email',
      avatarKey: 'avatar',
      isNearLabel: true,
      valueKey: 'id',
      labelKey: 'name',
      isRequired: false,
      fieldName: 'reporting_manager',
    },
    {
      name: 'ats_department_id',
      label: 'Department',
      options: departments,
      type: 'kSelect',
      isClearable: false,
      valueKey: 'id',
      labelKey: 'name',
      components: {
        Menu: SelectParentChildRootOption,
        SingleValue: DepartmentSingleValue,
      },
      isRequired: false,
      fieldName: 'ats_department',
    },
    {
      name: 'ats_office_id',
      label: 'Office',
      options: offices,
      type: 'kSelect',
      isClearable: false,
      valueKey: 'id',
      labelKey: 'name',
      // Full office address as grey text under the name; `location` comes from
      // the ats_offices payload. Remote offices keep the tag via remoteKey default.
      descKey: 'location',
      components: { Option: SelectWithDescriptionOption },
      isRequired: false,
      fieldName: 'ats_office',
    },
    isNewOfferFields && !userHasReqEnabled
      ? {
          name: 'req_id',
          label: 'Req ID',
          type: 'kInput',
          isRequired: false,
          fieldName: 'req_id',
        }
      : null,
    {
      type: 'custom',
      Field: () => {
        const [, { value: officeVal }] = useField('ats_office_id');
        const [, { value: departVal }] = useField('ats_department_id');

        useEffect(() => {
          if (filterData.offices !== officeVal) {
            setFilterData((prev) => ({
              ...prev,
              offices: officeVal || undefined,
            }));
          }
        }, [officeVal]);

        useEffect(() => {
          if (filterData.departments !== departVal) {
            setFilterData((prev) => ({
              ...prev,
              departments: departVal || undefined,
            }));
          }
        }, [departVal]);
      },
      skipValidation: true,
    },
  ];

  const processedJobFields = applyFieldDetails(
    jobFields,
    'job_details',
    getFieldDetails
  );

  const orderedJobFields = reorderFieldsByStaticFields(
    processedJobFields,
    'job_details'
  );

  const defaultSectionConfigs = [
    { id: 'offer_details', fields: orderedOfferDetailsFields },
    {
      id: 'additional_compensation',
      fields: orderedAdditionalCompensationFields,
    },
    { id: 'candidate_details', fields: orderedCandidateFields },
    { id: 'job_details', fields: orderedJobFields },
  ].map(({ id, fields }) => ({
    id,
    sectionId: SECTION_DOM_ID_BY_ID[id],
    title: SECTION_LABEL_BY_ID[id],
    fields,
  }));

  // Multi-value: multiple FE entries can share an identifier (e.g. conditional `amount` for units vs money). Caller filters by form value downstream.
  const fieldsByIdentifier = useMemo(() => {
    if (!offerFieldReorderEnabled) return new Map<string, any[]>();
    const map = new Map<string, any[]>();
    [
      ...offerDetailsFields,
      ...additionalCompensationFields,
      ...candidateFields,
      ...jobFields,
    ]
      .filter(Boolean)
      .forEach((f) => {
        const ident = f?.fieldName || f?.name;
        if (!ident) return;
        const arr = map.get(String(ident)) || [];
        arr.push(f);
        map.set(String(ident), arr);
      });
    return map;
  }, [
    offerFieldReorderEnabled,
    offerDetailsFields,
    additionalCompensationFields,
    candidateFields,
    jobFields,
  ]);
  const customFieldById = useMemo(() => {
    if (!offerFieldReorderEnabled) return new Map<string, any>();
    const map = new Map<string, any>();
    (customForm || []).forEach((f) => {
      const m = String(f?.name || '').match(/^custom_(.+)$/);
      if (m) map.set(m[1], f);
    });
    return map;
  }, [offerFieldReorderEnabled, customForm]);

  // followsField: 'X' → FE-only follower widgets keyed by their parent's identifier.
  // Used to pin followers (e.g. salary above-range warning) adjacent to their
  // parent under reorder-on, where BE doesn't emit the follower entry.
  const followersByParent = useMemo(() => {
    if (!offerFieldReorderEnabled) return new Map<string, any[]>();
    const map = new Map<string, any[]>();
    [
      ...offerDetailsFields,
      ...additionalCompensationFields,
      ...candidateFields,
      ...jobFields,
    ]
      .filter(Boolean)
      .forEach((f) => {
        const parent = f?.followsField;
        if (!parent) return;
        const arr = map.get(String(parent)) || [];
        arr.push(f);
        map.set(String(parent), arr);
      });
    return map;
  }, [
    offerFieldReorderEnabled,
    offerDetailsFields,
    additionalCompensationFields,
    candidateFields,
    jobFields,
  ]);

  // BE field → FE field(s). Custom: cf lookup. Static: identifier lookup with isRequired overlay.
  const buildFieldsFromBeF = useCallback(
    (beF: any) => {
      // Carry BE's re-approval flag onto the FE field so the empty-tokens warning +
      // Details form can flag fields whose edit requires re-approval (CORE-4672).
      const preapprovalEnabled = !!beF?.preapproval_enabled;
      if (beF?.field_kind === FIELD_KIND.CUSTOM) {
        const cfId = beF?.ats_custom_field_id ?? beF?.id;
        const cf = customFieldById.get(String(cfId));
        return cf ? [{ ...cf, preapproval_enabled: preapprovalEnabled }] : [];
      }
      const ident = BE_ID_TO_FE_IDENTIFIER[String(beF?.id)] || String(beF?.id);
      const tokenName = beF?.variable ? `offer.${beF.variable}` : undefined;
      const resolved = (fieldsByIdentifier.get(ident) || []).map(
        (feF: any) => ({
          ...feF,
          // skipValidation fields opt out of BE-driven isRequired override (e.g.
          // candidate_email has its own Yup rule). tokenName must still be set so
          // getFilledValues can track the value for template token matching.
          ...(feF?.skipValidation ? {} : { isRequired: !!beF?.required }),
          tokenName: tokenName || feF?.tokenName,
          preapproval_enabled: preapprovalEnabled,
        })
      );
      // award_type is an FE-only switching widget for equity_amount; the two
      // conditional `amount` entries gate on values.award_type. BE never emits
      // award_type, so under cross-section drag of equity_amount the radio
      // vanishes and the amount input never renders. Pin it adjacent so the
      // pair travels together.
      const peerPrepend =
        ident === 'amount' ? fieldsByIdentifier.get('award_type') || [] : [];
      // followsField entries (e.g. salary above-range warning) are FE-only and
      // not emitted by BE. Append them after the parent so they travel together
      // when the parent is moved across sections.
      const followers = followersByParent.get(ident) || [];
      return [...peerPrepend, ...resolved, ...followers];
    },
    [customFieldById, fieldsByIdentifier, followersByParent]
  );

  // Field list inside a single named section, with a custom predicate.
  const fieldsInSection = (sectionId: string, predicate: (f: any) => boolean) =>
    (offerStaticFields || [])
      .filter((s) => s?.id === sectionId)
      .flatMap((s) => (s?.fields || []).filter(predicate));

  const customSectionFields = useMemo(
    () =>
      offerFieldReorderEnabled
        ? fieldsInSection(SECTION_ID.CUSTOM, (f) => !f?.hidden).flatMap(
            buildFieldsFromBeF
          )
        : customForm || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      offerFieldReorderEnabled,
      offerStaticFields,
      buildFieldsFromBeF,
      customForm,
    ]
  );
  const customFields = [customFieldsWrapper, ...customSectionFields];

  const sectionConfigs = offerFieldReorderEnabled
    ? (offerStaticFields || [])
        .filter((beSec) => beSec?.id !== SECTION_ID.CUSTOM)
        .map((beSec) => {
          const resolved = (beSec?.fields || [])
            .filter((f) => !f?.hidden)
            .flatMap(buildFieldsFromBeF)
            .filter(Boolean);
          // requisitionFields pin at the top of offer_details.
          const fields =
            beSec.id === SECTION_ID.OFFER_DETAILS
              ? [...requisitionFields, ...resolved]
              : resolved;
          return {
            id: beSec.id,
            sectionId: SECTION_DOM_ID_BY_ID[beSec.id] || `${beSec.id}Section`,
            title: SECTION_LABEL_BY_ID[beSec.id] || beSec.label || beSec.id,
            fields,
            // resolvedCount ignores requisitionFields wrappers so an
            // offer_details with only the req wrapper (recruiter moved all
            // its fields elsewhere) is treated as empty (CORE-4849).
            resolvedCount: resolved.length,
            enabled: beSec.enabled !== false,
          };
        })
        .filter(
          (sc) =>
            sc.resolvedCount > 0 ||
            (sc.id === SECTION_ID.OFFER_DETAILS && userHasReqEnabled)
        )
    : defaultSectionConfigs;

  const detailsFormConfig = [
    ...sectionConfigs.map(({ id, sectionId, title, fields, enabled }) => {
      const staticFieldSection = offerStaticFields?.find(
        (section) => section.id === id
      );
      // Under flag-on, sectionConfigs already contains the BE-determined field
      // set for this section — use its length so the wrapper visibility +
      // header count match what actually renders.
      const hasVisibleFields = offerFieldReorderEnabled
        ? (fields || []).length > 0
        : staticFieldSection?.fields?.some((i) => !i.hidden);
      const fieldCount = offerFieldReorderEnabled
        ? (fields || []).length
        : staticFieldSection?.fields?.filter((i) => !i.hidden).length || 0;

      // additional_compensation can be toggled off in settings — under flag-on
      // the BE-derived `enabled` is carried on sectionConfigs; under flag-off
      // fall back to the raw BE response's enabled key.
      const isAdditionalComp = id === SECTION_ID.ADDITIONAL_COMPENSATION;
      const legacyAdditionalCompEnabled =
        staticFieldSection && 'enabled' in staticFieldSection
          ? staticFieldSection.enabled === true
          : true;
      const additionalCompEnabled = offerFieldReorderEnabled
        ? enabled !== false
        : legacyAdditionalCompEnabled;
      const isSectionEnabled = isAdditionalComp ? additionalCompEnabled : true;

      return isNewOfferFields
        ? hasVisibleFields &&
            isSectionEnabled && {
              id: sectionId,
              fields,
              type: 'wrapper',
              Wrapper: SectionWithHeading,
              wrapperProps: {
                title,
                count: fieldCount,
                ...offerSectionProps(isOfferV1),
              },
            }
        : {
            id: sectionId,
            fields,
            type: 'wrapper',
            Wrapper: SectionWithHeading,
            wrapperProps: {
              title,
              ...offerSectionProps(isOfferV1),
            },
          };
    }),
    customFields?.length > 0 && {
      id: 'customFields',
      fields: customFields,
      type: 'wrapper',
      Wrapper: customFields?.length > 1 ? SectionWithHeading : undefined,
      wrapperProps:
        customFields?.length > 1
          ? {
              title: 'Additional details',
              count: customFields?.length - 1,
              ...offerSectionProps(isOfferV1),
            }
          : undefined,
    },
  ].filter(Boolean);

  const statusConfig = {
    unverified: 'This template has un unverified tokens',
    error: 'This template has invalid tokens',
    verified: 'All tokens verified',
    no_tokens: 'No tokens found',
  };

  const templatesFormConfig = [
    {
      type: 'kSelect',
      name: 'ats_offer_template_id',
      label: 'Select template',
      placeholder: 'Select template',
      isRequired: true,
      options: [...templates]
        ?.sort((a, _b) => (a?.status === 'verified' ? -1 : 1))
        .map((temp) => ({
          label: temp?.name,
          value: temp?.id,
          desc:
            Object.keys(temp?.tokens)?.length === 0
              ? statusConfig['no_tokens']
              : statusConfig[temp?.status],
          isDisabled: temp?.status !== 'verified',
        })),
      descKey: 'desc',
      components: {
        Option: SelectWithDescriptionOption,
      },
    },
    {
      type: 'custom',
      Field: () => {
        const [_field, { value }] = useField('ats_offer_template_id');
        const [currentTemplate, setTemplate] = useState({
          tokens: {},
          id: undefined,
        });

        useEffect(() => {
          const template = templates?.find((i) => i?.id === value);
          setTemplate((prev) => (prev?.id !== template?.id ? template : prev));
        }, [value]);

        const validTokens = [];
        const inValidTokens = [];

        Object.entries(currentTemplate?.tokens || {}).forEach(
          ([key, value]) => {
            if (value === 'invalid') {
              inValidTokens.push(key);
            }

            if (value === 'valid') {
              validTokens.push(key);
            }
          }
        );

        const validLabel = 'Tokens included in this template';
        const invalidLabel =
          'Invalid tokens found. Please fix them before continuing';
        if (!currentTemplate?.id) return null;

        return (
          <>
            <Link
              fontSize="12px"
              fontWeight="700"
              color="ats-primary"
              cursor="pointer"
              _hover={{
                textDecoration: 'underline',
              }}
              isExternal
              boxShadow="none"
              _focus={{ boxShadow: 'none' }}
              href={`/settings/offer-letter-templates/${currentTemplate?.id}`}
            >
              <Flex mb="24px">
                Edit this template
                <LinkExternal01
                  size="1.25em"
                  color="var(--kula-colors-ats-primary)"
                  className="kula-icon"
                  style={{ marginLeft: '6px' }}
                />
              </Flex>
            </Link>
            <Flex gap="10px" flexDir="column">
              {validTokens?.length > 0 ? (
                <>
                  <KText variant="heading_3">{validLabel}</KText>
                  <Flex gap="10px" flexWrap="wrap">
                    {validTokens.map((token) => {
                      const label = `{{${token}}}`;
                      return <KTag key={token} value={label}></KTag>;
                    })}
                  </Flex>
                </>
              ) : (
                <>
                  <Text fontSize="sm">No custom tokens in this template</Text>
                  <EmptyResults
                    icon={NoTokens}
                    wrapperProps={{ mt: '30px' }}
                    text={'No tokens found'}
                    iconProps={{ h: '140px', w: '142px', mb: '0px' }}
                    subText="LOOKS EMPTY IN HERE"
                  />
                </>
              )}
            </Flex>
            {inValidTokens?.length > 0 && (
              <Flex mt="10px" gap="10px" flexDir="column">
                <Text size="md-bold">{invalidLabel}</Text>
                <Flex gap="10px" flexWrap="wrap">
                  {inValidTokens.map((token) => (
                    <Text
                      key={token}
                      color="ats-text-secondary"
                      bgColor="ats-tertiary"
                      borderRadius="sm"
                      border="1px solid"
                      borderColor="transparent"
                      size="sm-semi-bold"
                      p="0.125em 0.5em"
                    >
                      {`{{${token}}}`}
                    </Text>
                  ))}
                </Flex>
              </Flex>
            )}
          </>
        );
      },
    },
    {
      type: 'custom',
      Field: ({ formik: _formik }) => {
        // useEffect(() => {
        //   if (tabIndex === 1) {
        //     const currentVal = formik?.isValid;
        //     const currentValDraft = formik?.isValid && formik?.dirty;
        //     setFormValidity((prev) => {
        //       if (
        //         prev.template !== currentVal ||
        //         prev.templateDraft !== currentValDraft
        //       ) {
        //         return {
        //           ...prev,
        //           template: currentVal,
        //           templateDraft: currentValDraft,
        //         };
        //       } else {
        //         return prev;
        //       }
        //     });
        //   }
        // }, [formik]);
      },
    },
  ];

  return {
    detailsFormConfig,
    templatesFormConfig,
    // Scoped fields the form renders — callers build cf-id/row-id maps from these.
    atsFormFields: ats_form_fields,
  };
};
